/**
 * TrueCare Partners — build-time brand configuration.
 *
 * Per Q4 (`docs/decisions/q04-tenancy-and-branding.md`): this file is the
 * SINGLE source of truth for every brand-affecting value in the codebase.
 * Rebrand = edit this file + commit + deploy. No DB-backed brand config,
 * no admin UI for branding.
 *
 * Visual tokens (colors, fonts) are extracted from the Q5 mockup artifact
 * (`docs/history/TrueCare_v5_h2h.html`); the artifact itself is reference-
 * only per Q5 and is NOT a code starting point.
 *
 * Imports:
 *   - `apps/marketing/*` consumes via `@truecare/ui/branding`
 *   - `apps/portal/*` consumes via `@truecare/ui/branding`
 *   - `apps/admin/*` consumes via `@truecare/ui/branding`
 *   - The Tailwind preset (`packages/ui/tailwind.preset.cjs`) duplicates
 *     the palette into Tailwind theme tokens; if you change a colour here,
 *     update the preset too. The duplication is unavoidable — Tailwind
 *     config is CJS-loaded at build time, can't import TS.
 *
 * Phase 14a-1 discipline — DO NOT add hostnames or URLs to this file.
 * Brand attributes (name, tagline, colors, fonts, contact phone/city) live
 * here; deployment-specific URLs — and the support email domain — flow
 * through env vars / per-app derivation, not this file:
 *   - Backend: `BRAND_DOMAIN` (drives session-cookie scope)
 *   - Frontend: `VITE_PORTAL_URL`, `VITE_ADMIN_URL`, `VITE_API_BASE_URL`
 *     (Vite-baked at build time) and Astro's `PUBLIC_*` equivalents
 * That way swapping a domain (e.g. `.ca` → `.cc`, or staging-vs-prod)
 * is a config change, not a brand-file edit.
 */
export const brand = {
    /** Product name shown in headers, emails, etc. */
    productName: 'TrueCare™ Partners',
    /** Short tagline used on the marketing home page hero. Also rendered as the
     *  site-wide <meta description> + og:description on every marketing page, so
     *  it is the copy that shows in search results + social shares.
     *  Q11 §3b — "care professionals", never "caregivers". */
    tagline: 'Ontario care staffing — facilities, care professionals, and families on one platform.',
    /** Contact info shown in marketing footer + transactional emails.
     *  NOTE: the support email is deliberately NOT baked here — its domain is
     *  DERIVED per-app so it tracks the deploy target (`.ca` prod / `.cc`
     *  staging): marketing derives from `PUBLIC_SITE_URL`
     *  (`apps/marketing/src/config/support.ts`); the portal derives from the
     *  window host (`apps/portal/src/support-email.ts`). A mismatched-domain
     *  address (e.g. a `.cc` email on the live `.ca` site) reads as a phishing
     *  red flag, so per Phase-14a-1 no env-specific hostname lives in shared
     *  config. */
    contact: {
        phone: '519-807-5671',
        city: 'Waterloo, Ontario',
    },
    /** Social media presence — rendered in the marketing footer's "Follow"
     *  section. Per docs/design/04-frontend-principles.md Principle 6:
     *  plain-text links, no icons. Each entry is `{ label, href }`; adding
     *  a new platform = one entry here, no Footer.astro edits needed. */
    socials: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/truecare-partners/' },
        { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61591021676123' },
        // `?si=…` share-tracking token stripped — it's a referral tag from YouTube's
        // share sheet, not part of the channel URL.
        { label: 'YouTube', href: 'https://youtube.com/@truecarepartner' },
    ],
    /** Q5-locked palette — keep in sync with the Tailwind preset. */
    colors: {
        /** Primary brand colour — forest green. Used for buttons, accents,
         *  links, focus rings, headings against light backgrounds. */
        forest: '#1E4A38',
        /** Page background + neutral surface — warm cream. */
        cream: '#FAF8F4',
        /** Secondary accent — antique gold. Used sparingly: link hover,
         *  highlighted prices, "premium" badges. */
        gold: '#8A6524',
        /** Foreground text — near-black for body. */
        ink: '#1A1A1A',
        /** Muted text for hints + secondary info. */
        inkMuted: '#5A5A5A',
        /** Hairline borders. */
        border: '#E2DCD2',
        /** Status colors (semantic). */
        success: '#2D7A4A',
        warning: '#8F4C15',
        danger: '#9F1F1F',
    },
    /** Q5-locked typography. */
    fonts: {
        /** Display + headings (h1, h2). */
        heading: '"Playfair Display", Georgia, serif',
        /** Body text + UI. */
        body: '"Source Sans 3", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        /** Logo wordmark — italic Georgia per the Q5 artifact. */
        logo: 'Georgia, "Playfair Display", serif',
        /** Code blocks, monospace data (rate cards, IDs in admin). */
        mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
    /** Default radius for cards / inputs / buttons. */
    radius: {
        sm: '0.25rem',
        md: '0.5rem',
        lg: '0.75rem',
    },
};
//# sourceMappingURL=branding.js.map